import React, { useState, useEffect, useRef } from 'react'
import { useAuth } from '../context/AuthContext.jsx'
import { useSearchParams } from 'react-router-dom'
import Icon from '../components/IconLibrary'
import Hls from 'hls.js'

export default function SongsPage() {
  const { user, tokens } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const videoId = searchParams.get('v')

  const [videos, setVideos] = useState([])
  const [currentVideo, setCurrentVideo] = useState(null)
  const [loading, setLoading] = useState(true)
  const [liking, setLiking] = useState(false)
  const [activeCategory, setActiveCategory] = useState('All')
  const [showSignInPrompt, setShowSignInPrompt] = useState(false)
  const [gradeTags, setGradeTags] = useState([])
  const videoRef = useRef(null)
  const hlsRef = useRef(null)
  const progressIntervalRef = useRef(null)
  const viewTrackedRef = useRef(false)
  const currentVideoIdRef = useRef(null)

  useEffect(() => {
    fetchVideos()
    fetchGradeTags()
  }, [])

  useEffect(() => {
    if (!videoId) {
      fetchVideos()
    }
  }, [activeCategory])

  useEffect(() => {
    if (videoId) {
      fetchVideoDetails(videoId)
    } else {
      setCurrentVideo(null)
      cleanupVideo()
    }
  }, [videoId])

  // Refresh video data when user logs in/out
  useEffect(() => {
    if (videoId && currentVideo) {
      fetchVideoDetails(videoId)
    }
  }, [user])

  // Initialize HLS player when video ID changes
  useEffect(() => {
    if (!currentVideo || !videoRef.current) return
    
    // Only reinitialize if it's actually a different video
    const isNewVideo = currentVideoIdRef.current !== currentVideo.id
    
    if (isNewVideo) {
      currentVideoIdRef.current = currentVideo.id
      cleanupVideo()
      viewTrackedRef.current = false // Reset for new video
      
      const masterUrl = `/hls/${currentVideo.id}/master.m3u8`
      
      if (Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: false,
        })
        hlsRef.current = hls
        hls.loadSource(masterUrl)
        hls.attachMedia(videoRef.current)
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          videoRef.current?.play()
        })
        hls.on(Hls.Events.ERROR, (event, data) => {
          if (data.fatal) {
            console.error('HLS fatal error:', data)
          }
        })
      } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
        // Native HLS support (Safari)
        videoRef.current.src = masterUrl
        videoRef.current.play()
      }

      // Track view on initial load (only once)
      if (!viewTrackedRef.current) {
        viewTrackedRef.current = true
        trackView(currentVideo.id)
      }

      // Start progress tracking
      startProgressTracking()
    }

    return () => {
      if (isNewVideo) {
        cleanupVideo()
      }
    }
  }, [currentVideo?.id])

  const cleanupVideo = () => {
    if (hlsRef.current) {
      hlsRef.current.destroy()
      hlsRef.current = null
    }
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current)
      progressIntervalRef.current = null
    }
  }

  const trackView = async (id) => {
    try {
      await fetch(`/api/videos/${id}/view/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(tokens?.accessToken ? { Authorization: `Bearer ${tokens.accessToken}` } : {})
        },
        body: JSON.stringify({ watchedDuration: 0 })
      })
    } catch (e) {
      console.error('Failed to track view:', e)
    }
  }

  const startProgressTracking = () => {
    // Track progress every 10 seconds
    progressIntervalRef.current = setInterval(() => {
      if (videoRef.current && currentVideo) {
        const watched = Math.floor(videoRef.current.currentTime)
        if (watched > 0) {
          fetch(`/api/videos/${currentVideo.id}/view/`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(tokens?.accessToken ? { Authorization: `Bearer ${tokens.accessToken}` } : {})
            },
            body: JSON.stringify({ watchedDuration: watched })
          })
        }
      }
    }, 10000)
  }

  const fetchGradeTags = async () => {
    try {
      const res = await fetch('/api/admin/videos/tags/values?type=grade', {
        headers: { Authorization: `Bearer ${tokens?.accessToken || ''}` }
      })
      const data = await res.json()
      if (data.success) {
        setGradeTags(data.data.values || [])
      }
    } catch (e) {
      console.error('Failed to fetch grade tags:', e)
    }
  }

  const fetchVideos = async () => {
    setLoading(true)
    try {
      let url = '/api/videos/?limit=50'
      
      // Check if it's a grade filter
      if (activeCategory.startsWith('grade:')) {
        const gradeValue = activeCategory.replace('grade:', '')
        url = '/api/videos/?limit=50'
        
        const res = await fetch(url, {
          headers: tokens?.accessToken ? { Authorization: `Bearer ${tokens.accessToken}` } : {}
        })
        const data = await res.json()
        if (data.success) {
          // Filter videos by grade tag on client side
          const filtered = data.data.videos.filter(video => 
            video.tags && video.tags.some(tag => tag.tagType === 'grade' && tag.tagValue === gradeValue)
          )
          setVideos(filtered)
        }
        setLoading(false)
        return
      }
      
      // Adjust URL based on active category
      switch (activeCategory) {
        case 'Favorites':
          if (!user) {
            setVideos([])
            setLoading(false)
            return
          }
          url = '/api/videos/user/liked/?limit=50'
          break
        case 'Recent':
          url = '/api/videos/?limit=50&sort=newest'
          break
        case 'Popular':
          url = '/api/videos/?limit=50&sort=most_viewed'
          break
        case 'Watched':
          if (!user) {
            setVideos([])
            setLoading(false)
            return
          }
          url = '/api/videos/user/watched/?limit=50'
          break
        case 'Unwatched':
          if (!user) {
            setVideos([])
            setLoading(false)
            return
          }
          url = '/api/videos/user/unwatched/?limit=50'
          break
        case 'All':
        default:
          url = '/api/videos/?limit=50'
          break
      }

      const res = await fetch(url, {
        headers: tokens?.accessToken ? { Authorization: `Bearer ${tokens.accessToken}` } : {}
      })
      const data = await res.json()
      if (data.success) setVideos(data.data.videos)
    } catch (e) {
      console.error('Failed to fetch videos:', e)
    } finally {
      setLoading(false)
    }
  }

  const fetchVideoDetails = async (id) => {
    try {
      const res = await fetch(`/api/videos/${id}/`, {
        headers: tokens?.accessToken ? { Authorization: `Bearer ${tokens.accessToken}` } : {}
      })
      const data = await res.json()
      if (data.success) setCurrentVideo(data.data.video)
    } catch (e) {
      console.error('Failed to fetch video:', e)
    }
  }

  const toggleLike = async () => {
    if (!currentVideo) return
    
    // Show sign-in prompt if not logged in
    if (!user) {
      setShowSignInPrompt(true)
      return
    }
    
    setLiking(true)
    try {
      const method = currentVideo.userHasLiked ? 'DELETE' : 'POST'
      const res = await fetch(`/api/videos/${currentVideo.id}/like/`, {
        method,
        headers: { Authorization: `Bearer ${tokens.accessToken}` }
      })
      const data = await res.json()
      if (data.success) {
        setCurrentVideo(prev => ({ 
          ...prev, 
          userHasLiked: data.data.userHasLiked, 
          userHasDisliked: data.data.userHasDisliked,
          likeCount: data.data.likeCount 
        }))
        // Update in grid too
        setVideos(prev => prev.map(v => v.id === currentVideo.id ? { 
          ...v, 
          userHasLiked: data.data.userHasLiked, 
          userHasDisliked: data.data.userHasDisliked,
          likeCount: data.data.likeCount 
        } : v))
      }
    } catch (e) {
      console.error('Failed to toggle like:', e)
    } finally {
      setLiking(false)
    }
  }

  const toggleDislike = async () => {
    if (!currentVideo) return
    
    // Show sign-in prompt if not logged in
    if (!user) {
      setShowSignInPrompt(true)
      return
    }
    
    setLiking(true)
    try {
      const method = currentVideo.userHasDisliked ? 'DELETE' : 'POST'
      const url = currentVideo.userHasDisliked 
        ? `/api/videos/${currentVideo.id}/like/` 
        : `/api/videos/${currentVideo.id}/dislike/`
      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${tokens.accessToken}` }
      })
      const data = await res.json()
      if (data.success) {
        setCurrentVideo(prev => ({ 
          ...prev, 
          userHasLiked: data.data.userHasLiked, 
          userHasDisliked: data.data.userHasDisliked,
          likeCount: data.data.likeCount 
        }))
        // Update in grid too
        setVideos(prev => prev.map(v => v.id === currentVideo.id ? { 
          ...v, 
          userHasLiked: data.data.userHasLiked, 
          userHasDisliked: data.data.userHasDisliked,
          likeCount: data.data.likeCount 
        } : v))
      }
    } catch (e) {
      console.error('Failed to toggle dislike:', e)
    } finally {
      setLiking(false)
    }
  }

  const formatDuration = (sec) => {
    if (!sec) return '0:00'
    const m = Math.floor(sec / 60)
    const s = sec % 60
    return `${m}:${s.toString().padStart(2, '0')}`
  }

  const formatViews = (count) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`
    return count.toString()
  }

  const formatTimeAgo = (date) => {
    const now = new Date()
    const then = new Date(date)
    const diffMs = now - then
    const diffMins = Math.floor(diffMs / 60000)
    if (diffMins < 60) return `${diffMins} minutes ago`
    const diffHours = Math.floor(diffMins / 60)
    if (diffHours < 24) return `${diffHours} hours ago`
    const diffDays = Math.floor(diffHours / 24)
    return `${diffDays} days ago`
  }

  if (loading) {
    return <div style={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}><p>Loading videos...</p></div>
  }

  // Grid view
  if (!videoId) {
    const categories = [
      { id: 'All', label: 'All' },
      { id: 'Favorites', label: 'Favorites' },
      { id: 'Recent', label: 'Recently uploaded' },
      { id: 'Watched', label: 'Watched' },
      { id: 'Unwatched', label: 'New to you' },
      { id: 'Popular', label: 'Popular' },
      ...gradeTags.map(tag => ({ id: `grade:${tag}`, label: tag }))
    ]

    return (
      <div style={{ maxWidth: 1800, margin: '0 auto', padding: '24px 20px' }}>
        {/* Category chips */}
        <div style={{ 
          display: 'flex', 
          gap: 12, 
          marginBottom: 24, 
          overflowX: 'auto',
          paddingBottom: 8,
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}>
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              style={{
                padding: '8px 16px',
                borderRadius: 8,
                border: 'none',
                background: activeCategory === cat.id 
                  ? 'color-mix(in oklab, var(--bg), var(--fg) 15%)' 
                  : 'color-mix(in oklab, var(--bg), var(--fg) 6%)',
                color: 'var(--fg)',
                fontSize: 14,
                fontWeight: activeCategory === cat.id ? 600 : 500,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'all 0.15s',
                flexShrink: 0
              }}
              onMouseEnter={(e) => {
                if (activeCategory !== cat.id) {
                  e.currentTarget.style.background = 'color-mix(in oklab, var(--bg), var(--fg) 10%)'
                }
              }}
              onMouseLeave={(e) => {
                if (activeCategory !== cat.id) {
                  e.currentTarget.style.background = 'color-mix(in oklab, var(--bg), var(--fg) 6%)'
                }
              }}
            >
              {cat.label}
            </button>
          ))}
        </div>

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(320px, 100%), 1fr))', 
          gap: 16 
        }}>
          {videos.map(video => (
            <div
              key={video.id}
              onClick={() => setSearchParams({ v: video.id })}
              style={{ cursor: 'pointer', display: 'grid', gap: 12 }}
            >
              <div style={{ position: 'relative', paddingBottom: '56.25%', background: '#000', borderRadius: 12, overflow: 'hidden' }}>
                {video.thumbnail ? (
                  <img src={video.thumbnail} alt={video.title} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: '#666' }}>No thumbnail</div>
                )}
                <div style={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(0,0,0,0.8)', color: '#fff', padding: '2px 6px', borderRadius: 4, fontSize: 12, fontWeight: 600 }}>
                  {formatDuration(video.duration)}
                </div>
              </div>
              <div style={{ display: 'grid', gap: 4 }}>
                <h3 style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{video.title}</h3>
                <div style={{ fontSize: 13, color: 'var(--muted)' }}>
                  {formatViews(video.viewCount)} views • {formatTimeAgo(video.createdAt)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Player view
  if (!currentVideo) {
    return <div style={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}><p>Loading video...</p></div>
  }

  const relatedVideos = videos.filter(v => v.id !== currentVideo.id).slice(0, 12)

  return (
    <div style={{ 
      display: 'grid', 
      gridTemplateColumns: window.innerWidth > 1024 ? '1fr 400px' : '1fr', 
      gap: 24, 
      maxWidth: 1800, 
      margin: '0 auto', 
      padding: window.innerWidth > 768 ? '24px 20px' : '12px 12px' 
    }}>
      {/* Main player area */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ position: 'relative', width: '100%', maxHeight: '70vh', aspectRatio: '16/9', background: '#000', borderRadius: '12px', overflow: 'hidden' }}>
          <video
            ref={videoRef}
            controls
            playsInline
            preload="metadata"
            crossOrigin="anonymous"
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          >
            Your browser does not support video playback.
          </video>
        </div>

        {/* Video title - centered */}
        <h1 style={{ fontSize: 18, fontWeight: 600, lineHeight: 1.4, margin: 0, textAlign: 'center' }}>{currentVideo.title}</h1>

        <div style={{ display: 'grid', gap: 16 }}>
          {/* Info row with avatar, uploader info, and actions */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
            {/* Left side: Avatar + Uploader info */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
              {/* Avatar circle */}
              <div style={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: 16,
                fontWeight: 600,
                flexShrink: 0
              }}>
                {currentVideo.uploader?.username?.[0]?.toUpperCase() || currentVideo.uploader?.displayName?.[0]?.toUpperCase() || 'U'}
              </div>
              
              {/* Uploader name and view stats */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                {currentVideo.uploader && (currentVideo.uploader.displayName || currentVideo.uploader.username) && (
                  <div style={{ fontSize: 14, fontWeight: 500, lineHeight: 1.4 }}>
                    {currentVideo.uploader.displayName || currentVideo.uploader.username}
                  </div>
                )}
                <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.4 }}>
                  {formatViews(currentVideo.viewCount)} views • {formatTimeAgo(currentVideo.createdAt)}
                </div>
              </div>
            </div>
            
            {/* Right side: Like/Dislike Pill */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              background: 'color-mix(in oklab, var(--bg), var(--fg) 8%)',
              borderRadius: 24,
              overflow: 'hidden'
            }}>
              <button
                onClick={toggleLike}
                disabled={liking}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '10px 16px',
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--fg)',
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: user ? 'pointer' : 'not-allowed',
                  opacity: liking ? 0.6 : 1,
                  transition: 'opacity 0.15s'
                }}
              >
                <Icon name={currentVideo.userHasLiked ? 'hand.thumbsup.fill' : 'hand.thumbsup'} size={20} />
                <span>{currentVideo.likeCount || 0}</span>
              </button>
              
              <div style={{ width: 1, height: 24, background: 'color-mix(in oklab, var(--bg), var(--fg) 15%)' }} />
              
              <button
                onClick={toggleDislike}
                disabled={liking}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '10px 16px',
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--fg)',
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: user ? 'pointer' : 'not-allowed',
                  opacity: liking ? 0.6 : 1,
                  transition: 'opacity 0.15s'
                }}
              >
                <Icon name={currentVideo.userHasDisliked ? 'hand.thumbsdown.fill' : 'hand.thumbsdown'} size={20} />
              </button>
            </div>
          </div>

          {/* Category chips */}
          <div style={{ 
            display: 'flex', 
            gap: 12, 
            overflowX: 'auto',
            paddingBottom: 8,
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}>
            {[
              { id: 'All', label: 'All' },
              { id: 'Favorites', label: 'Favorites' },
              { id: 'Recent', label: 'Recently uploaded' },
              { id: 'Watched', label: 'Watched' },
              { id: 'Unwatched', label: 'New to you' },
              { id: 'Popular', label: 'Popular' },
              ...gradeTags.map(tag => ({ id: `grade:${tag}`, label: tag }))
            ].map(cat => (
              <button
                key={cat.id}
                onClick={() => {
                  setActiveCategory(cat.id)
                  setSearchParams({}) // Clear video ID to go back to grid
                }}
                style={{
                  padding: '8px 16px',
                  borderRadius: 8,
                  border: 'none',
                  background: activeCategory === cat.id 
                    ? 'color-mix(in oklab, var(--bg), var(--fg) 15%)' 
                    : 'color-mix(in oklab, var(--bg), var(--fg) 6%)',
                  color: 'var(--fg)',
                  fontSize: 14,
                  fontWeight: activeCategory === cat.id ? 600 : 500,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.15s',
                  flexShrink: 0
                }}
                onMouseEnter={(e) => {
                  if (activeCategory !== cat.id) {
                    e.currentTarget.style.background = 'color-mix(in oklab, var(--bg), var(--fg) 10%)'
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeCategory !== cat.id) {
                    e.currentTarget.style.background = 'color-mix(in oklab, var(--bg), var(--fg) 6%)'
                  }
                }}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Description */}
          {currentVideo.description && (
            <div style={{ 
              padding: 12, 
              background: 'color-mix(in oklab, var(--bg), var(--fg) 4%)', 
              borderRadius: 12, 
              fontSize: 14, 
              lineHeight: 1.6, 
              whiteSpace: 'pre-wrap',
              marginTop: 4
            }}>
              <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 8 }}>
                {formatViews(currentVideo.viewCount)} views  {formatTimeAgo(currentVideo.createdAt)}
              </div>
              {currentVideo.description}
            </div>
          )}
        </div>
      </div>

      {/* Sidebar - hidden on mobile, shown on desktop */}
      {window.innerWidth > 1024 && (
        <div style={{ display: 'grid', gap: 12, alignContent: 'start' }}>
          {relatedVideos.map(video => (
            <div
              key={video.id}
              onClick={() => setSearchParams({ v: video.id })}
              style={{ display: 'grid', gridTemplateColumns: '168px 1fr', gap: 12, cursor: 'pointer', borderRadius: 8, padding: 8, transition: 'background 0.15s' }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'color-mix(in oklab, var(--bg), var(--fg) 4%)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ position: 'relative', paddingBottom: '56.25%', background: '#000', borderRadius: 8, overflow: 'hidden' }}>
                {video.thumbnail ? (
                  <img src={video.thumbnail} alt={video.title} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: '#666', fontSize: 11 }}>No thumb</div>
                )}
                <div style={{ position: 'absolute', bottom: 4, right: 4, background: 'rgba(0,0,0,0.8)', color: '#fff', padding: '1px 4px', borderRadius: 3, fontSize: 11, fontWeight: 600 }}>
                  {formatDuration(video.duration)}
                </div>
              </div>
              <div style={{ display: 'grid', gap: 4, alignContent: 'start' }}>
                <h4 style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', margin: 0 }}>{video.title}</h4>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                  {formatViews(video.viewCount)} views
                </div>
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                  {formatTimeAgo(video.createdAt)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Mobile related videos - shown below player on mobile */}
      {window.innerWidth <= 1024 && (
        <div style={{ display: 'grid', gap: 12, marginTop: 24 }}>
          <h3 style={{ fontSize: 18, fontWeight: 600 }}>Related Videos</h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: window.innerWidth > 640 ? 'repeat(2, 1fr)' : '1fr',
            gap: 12 
          }}>
            {relatedVideos.slice(0, 6).map(video => (
              <div
                key={video.id}
                onClick={() => setSearchParams({ v: video.id })}
                style={{ cursor: 'pointer', display: 'grid', gap: 8 }}
              >
                <div style={{ position: 'relative', paddingBottom: '56.25%', background: '#000', borderRadius: 8, overflow: 'hidden' }}>
                  {video.thumbnail ? (
                    <img src={video.thumbnail} alt={video.title} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: '#666', fontSize: 12 }}>No thumbnail</div>
                  )}
                  <div style={{ position: 'absolute', bottom: 4, right: 4, background: 'rgba(0,0,0,0.8)', color: '#fff', padding: '2px 6px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
                    {formatDuration(video.duration)}
                  </div>
                </div>
                <div style={{ display: 'grid', gap: 2 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', margin: 0 }}>{video.title}</h4>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                    {formatViews(video.viewCount)} views • {formatTimeAgo(video.createdAt)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sign In Prompt Modal */}
      {showSignInPrompt && (
        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div 
            onClick={() => setShowSignInPrompt(false)}
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(0,0,0,0.6)',
              backdropFilter: 'blur(4px)'
            }}
          />
          <div style={{
            position: 'relative',
            background: 'var(--bg)',
            borderRadius: 16,
            padding: '32px 24px',
            maxWidth: 400,
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 24px 80px rgba(0,0,0,0.4)'
          }}>
            <h3 style={{ 
              fontSize: 24, 
              fontWeight: 600, 
              marginBottom: 12,
              color: 'var(--fg)'
            }}>
              Like this video?
            </h3>
            <p style={{ 
              fontSize: 16, 
              color: 'var(--muted)', 
              marginBottom: 24,
              lineHeight: 1.5
            }}>
              Sign in to make your opinion count.
            </p>
            <button
              onClick={() => {
                setShowSignInPrompt(false)
                // Trigger click on navbar sign-in button
                const signinBtn = document.querySelector('.signin-btn')
                if (signinBtn) {
                  signinBtn.click()
                }
              }}
              style={{
                width: '100%',
                padding: '14px',
                background: 'var(--fg)',
                color: 'var(--bg)',
                border: 'none',
                borderRadius: 24,
                fontSize: 16,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            >
              Sign in
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
